<script setup lang="ts">
/**
 * 按钮 —— 变体移植自 React 设计稿的 `buttonVariants`。
 *
 * 为什么不用 el-button：EP 的按钮自带一套与本设计不一致的圆角/高度/色阶，
 * 逐条覆盖比重写更费劲。表格、表单校验这类重型组件才值得保留 EP。
 */
import { computed, useSlots, type Component } from 'vue'

import { cn } from '@/lib/utils'

type Variant = 'default' | 'outline' | 'secondary' | 'ghost' | 'destructive' | 'link'
type Size = 'default' | 'xs' | 'sm' | 'lg' | 'icon' | 'icon-sm'

interface Props {
  variant?: Variant
  size?: Size
  /** 提交中：禁用并显示转圈。**不要在外面另外套 disabled** —— 两处判断会漂移。 */
  loading?: boolean
  disabled?: boolean
  type?: 'button' | 'submit' | 'reset'
  /**
   * 前置图标组件。存在的唯一理由是兼容存量的 `:icon="Refresh"` 写法（36 处），
   * 迁移时不必把每个按钮都改成插槽形式。
   * 新代码优先用 `#icon` 插槽 —— 那样图标的尺寸与间距由调用方掌握。
   */
  icon?: Component | null
  class?: string
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'default',
  size: 'default',
  loading: false,
  disabled: false,
  type: 'button',
  icon: null,
  class: '',
})

const slots = useSlots()

const BASE =
  'inline-flex shrink-0 items-center justify-center rounded-lg border border-transparent text-sm font-medium whitespace-nowrap transition-all outline-none select-none ' +
  'focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 ' +
  'disabled:pointer-events-none disabled:opacity-50 ' +
  "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"

/**
 * 变体用映射表而不是 v-if 链：加一个变体只改这张表，模板不动。
 *
 * ## outline / ghost 的禁用态不走 `opacity-50`（AG-14）
 *
 * 深色下真机量过：outline 禁用钮整体半透明后，文字对底还有约 4.3:1，
 * 但**边框**混到 1.3:1 —— 按钮的形状消失了，只剩一行漂浮的灰字，
 * 这就是「保存（已保存态）几乎看不见」的来源。实底变体（default/secondary/
 * destructive）没这个病：底色本身是一大块，半透明后仍看得出是一枚按钮。
 *
 * 所以这两个变体改成**显式禁用配色**：文字降到 `muted-foreground`
 * （语义令牌两套主题各自校准 —— 深色约 8:1、浅色约 4.8:1，不必写 `dark:`），
 * 边框保持可见，整钮不再压透明。
 *
 * ★ `loading` 走同一个 disabled 通道（模板 `:disabled="disabled || loading"`），
 * 所以 outline 的「提交中」从半隐形变成「灰字 + 转圈」—— 可读性只升不降，
 * 且不会出现「主按钮 loading 时变灰」（default 不在本次范围里）。
 */
const VARIANTS: Record<Variant, string> = {
  default: 'bg-primary text-primary-foreground hover:bg-primary/90',
  outline:
    'border-border bg-background hover:bg-muted hover:text-foreground ' +
    'disabled:opacity-100 disabled:border-border/70 disabled:bg-transparent disabled:text-muted-foreground',
  secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
  // 启用态显式给 `text-foreground`：不给的话它继承容器的字色，而表格单元格本身
  // 就是 muted 档 —— 于是禁用态的 `text-muted-foreground` 与启用态同色，
  // 页面上「灰掉」完全看不出来（模型仓库的合并 / 量化按钮实测如此）。
  ghost:
    'text-foreground hover:bg-muted hover:text-foreground ' +
    'disabled:opacity-100 disabled:text-muted-foreground/55',
  destructive: 'bg-destructive/10 text-destructive hover:bg-destructive/20',
  link: 'text-primary underline-offset-4 hover:underline',
}

const SIZES: Record<Size, string> = {
  default: 'h-8 gap-1.5 px-3',
  xs: 'h-6 gap-1 px-2 text-xs',
  sm: 'h-7 gap-1 px-2.5 text-[0.8rem]',
  lg: 'h-9 gap-1.5 px-4',
  icon: 'size-8',
  'icon-sm': 'size-7',
}

const classes = computed(() => cn(BASE, VARIANTS[props.variant], SIZES[props.size], props.class))

/**
 * 纯图标按钮（`icon` / `icon-sm`）loading 时**只画转圈**，默认插槽里的图标不再一起画。
 *
 * 存量写法里图标多半直接放默认插槽（`<AppButton size="icon-sm"><RefreshCw/></AppButton>`，
 * 浏览数据库 / 数据连接 / MCP 市场四处），而模板只对 `#icon` 插槽做了 `!loading`
 * 的隐藏 —— 于是 28px 的方块里转圈和刷新图标并排挤着。
 * 文字按钮不受影响：转圈 + 文字本来就是设计的样子。
 */
const hideDefaultSlot = computed(() => props.loading && props.size.startsWith('icon'))
</script>

<template>
  <button :type="type" :class="classes" :disabled="disabled || loading">
    <svg v-if="loading" class="size-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" />
      <path class="opacity-90" fill="currentColor" d="M12 2a10 10 0 0 1 10 10h-3a7 7 0 0 0-7-7V2Z" />
    </svg>
    <component :is="icon" v-else-if="icon" class="size-4" />
    <slot v-if="slots.icon && !loading" name="icon" />
    <slot v-if="!hideDefaultSlot" />
  </button>
</template>
